import { getIn } from 'formik';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import DatePicker from 'react-date-picker';
import styled from 'styled-components';
import { Calendar, ErrorIconRed } from '../../assets';
import { Colors } from '../../constants';
import { ShowIf } from '../ShowIf/ShowIf';
import { useTealium } from '../../tealium/useTealium';

const ParentDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0 2.5%;
  border: 1px solid ${props => props.error ? Colors.Primary4 : Colors.GreyScale5};
  margin-bottom: ${props => props.error ? '20px' : '0px'};
  background: ${Colors.GreyScale1};
  border-radius: 8px;
  width: 600px;
  height: 60px;
`;

const Label = styled.span`
  position: absolute;
  width: inherit;
  max-width: 100%;
  font-size: 16px;
  margin: 25px 0 0 0;
  color: ${Colors.GreyScale13};
  transform: ${props => (props.focussed ? 'translateY(-20px) scale(0.75)' : '')};
  transform-origin: ${props => (props.focussed ? 'top left' : '')};
  padding: ${props => (props.focussed ? '0 0 0 60px' : '0 0 0 30px')};
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4,0,0.2,1);
  transition-duration: 150ms;
`;

const StyledDatePicker = styled(DatePicker)`

  &.react-date-picker{
    width: 100%;
    height: 100%;
    background: transparent;
    outline: none;
    border: none;
    display: flex;
    position: relative;

    & > :nth-child(1){
      color: ${props => props.focussed ? 'black' : 'transparent'};           // to change the color of / / of DatePicker
      border-radius: 4px;
      width: 100%;
      height: 65px;
      border: 0;
      background-color: transparent;

      & > :nth-child(1){

        position: relative;
        bottom: -6px;
        input {
          background-color: transparent;
          outline: none;
        }
      }

      & > :nth-child(2){

        position: relative;
        bottom: -2px;
        padding: 20px 0;
        input {
          background-color: transparent;
          outline: none;
        }
        display: flex;
        width: 30px;
      }
    }
      /* Access the internal calendar */
      .react-calendar {
        .react-calendar__tile--now {
          background: #9e9e9e !important;
        }
        .react-calendar__tile--now:enabled:hover {
          background: #9e9e9e !important;
        }
    }
  }

`;

const HR = styled.hr`
  width: 95%;
  border-radius: 2px;
  margin: 0;
  height: 0.5px;
  background-color: ${props => props.error ? Colors.Primary4 : Colors.GreyScale13};
  position: absolute;
  top: 65px;
  border: 0;
  display: none;
`;

const SubMessage = styled.div`
  font-size: 0.75rem;
  line-height: 0.75rem;
  margin-top: 0.25rem;
  display: flex;
  column-gap: 0.25rem;
  color: ${Colors.Primary4};
  width: 100%;
`;

const stringFormat = 'yyyy-MM-dd';
const stringFormatForMonthYearOnly = 'yyyy-MM';

const renderDatePicker = (field, form, showOnlyMonthYear, isFocussed, track, events, ...props) => {

  const setCalendarDate = (value, dateFormat) => {

    form.setFieldTouched(field.name, true);

    const formattedDate = value ? DateTime.fromJSDate(value).toFormat(dateFormat) : '';
    form.setFieldValue(field.name, formattedDate);
    form.validateForm(form.values);

    if (formattedDate) {
      track(events.formBuilder.fieldInteraction(window.location.pathname, field.name, formattedDate));
    }

  };

  const dateFormat = props[0]?.dateFormat ? props[0].dateFormat : 'yyyy/MM/dd';

  if (showOnlyMonthYear) {
    return (
      <StyledDatePicker
        onChange={(value) => {
          setCalendarDate(value, stringFormatForMonthYearOnly);
        }}
        maxDetail={'year'}
        name={field.name}
        focussed={isFocussed || !!field.value}
        calendarIcon={
          <img src={Calendar} className="icon-calendar" alt='calendarIcon' />
        }
        clearIcon={null}
        yearPlaceholder={isFocussed ? '----' : ''}
        monthPlaceholder={isFocussed ? '--' : ''}
        format="yyyy/MM"
        value={field.value ? DateTime.fromFormat(field.value, stringFormatForMonthYearOnly).toJSDate() : field.value}
        onCalendarClose={() => {
          form.setFieldTouched(field.name, true);
          form.validateForm(form.values);
        }}
      />);
  }

  return (
    <StyledDatePicker
      name={field.name}
      focussed={isFocussed || !!field.value}
      disabled={false}
      calendarIcon={
        <img src={Calendar} className="icon-calendar" alt='calendarIcon' />
      }
      clearIcon={null}
      yearPlaceholder={isFocussed ? '----' : ''}
      monthPlaceholder={isFocussed ? '--' : ''}
      dayPlaceholder={isFocussed ? '--' : ''}
      format={dateFormat}
      value={field.value ? DateTime.fromFormat(field.value, stringFormat).toJSDate() : field.value}
      onChange={(value) => { setCalendarDate(value, stringFormat); }}
      onCalendarClose={() => {
        form.setFieldTouched(field.name, true);
        form.validateForm(form.values);
      }}
      minDate={props.minDate ? props.minDate : null}
      maxDate={props.maxDate ? props.maxDate : null}
    />);
};

export const NewDateSelector = ({ field, form, showOnlyMonthYear = false, ...props }) => {
  const [isFocussed, setIsFocussed] = useState(false);
  const touched = useMemo(() => getIn(form?.touched, field.name), [form?.touched, field.name]);
  const error = useMemo(() => getIn(form?.errors, field.name), [form?.errors, field.name]);
  const { track, events } = useTealium();
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) { return; }
    const inputs = containerRef.current.querySelectorAll('input[name$="year"], input[name$="month"]');
    inputs.forEach(input => {
      const handler = (e) => {

        if (input.name.endsWith('year') && e.target.value.length > 4) {
          e.target.value = e.target.value.slice(0, 4);
        }

        if (e.target.value.length === 1 && e.target.value === '0') {
          e.target.value = '';
        }
      };
      input.addEventListener('input', handler);
      return () => input.removeEventListener('input', handler);
    });
  }, [isFocussed]);

  return (
    <ParentDiv
      ref={containerRef}
      onFocus={() => setIsFocussed(true)}
      onBlur={() => setIsFocussed(false)}
      error={touched && error}
    >
      <Label
        focussed={isFocussed || !!field.value}
      >
        {props.label}
      </Label>

      {renderDatePicker(field, form, showOnlyMonthYear, isFocussed, track, events, props)}

      <HR error={touched && error} />

      <ShowIf condition={!!(touched && error)}>
        <SubMessage>
          <img
            src={ErrorIconRed}
            alt="error"
          />
          {error}
        </SubMessage>
      </ShowIf>
    </ParentDiv>
  );
};

NewDateSelector.propTypes = {
  field: PropTypes.object,
  form: PropTypes.object,
  label: PropTypes.string,
  maxDate: PropTypes.any,
  minDate: PropTypes.any,
  showOnlyMonthYear: PropTypes.bool
};